import React, { useState, useEffect } from 'react';
import { getHubs, getHubSettings, updateHubGeneral, updateHubDeliveryFees, updateHubHandlingCharges, getHubPincodes, addHubPincode, removeHubPincode, togglePincodeFarArea, searchHubPincode, getCategories } from '../config/api';
import { toast } from 'react-toastify';
import useIsMobile from '../hooks/useIsMobile';
import { saveBlob } from '../utils/nativeFiles';

const TABS = ['General', 'Delivery Fees', 'Handling Charges', 'Delivery Pincodes'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DEFAULT_FEES_FOR = (name) => ({ sameDay: 0, nextPlus1: 0, nextPlus2: 0, farSurcharge: 0, quantum: 1 });
const DEFAULT_GENERAL = { hubName: '', hubAddress: '', openTime: '10:00', closeTime: '19:00', holidays: [], cutoffPanel: '15:00', cutoffInverter: '16:00', pickupCutoffPanel: '15:00', pickupCutoffInverter: '16:00', supportNumber: '', maxOrdersPerDay: 10, infoText: '', vehicleNumber: '' };
const DEFAULT_FEES = {};
const DEFAULT_HANDLING = {};
const TH = { padding: '10px 12px', background: '#f8f9fa', fontWeight: '600', fontSize: '12px', color: '#495057', textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '1px solid #dee2e6', textAlign: 'left' };
const TD = { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' };

const ST = ({ c }) => <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#212529', marginBottom: '14px', paddingBottom: '8px', borderBottom: '1px solid #e9ecef' }}>{c}</h3>;
const FG = ({ label, hint, children }) => <div className="form-group"><label className="form-label">{label}</label>{children}{hint && <small style={{ color: '#6c757d', fontSize: '11px' }}>{hint}</small>}</div>;
const InfoBox = ({ title, desc }) => <div style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '6px', padding: '14px' }}><div style={{ fontWeight: '600', fontSize: '13px', color: '#212529', marginBottom: '8px' }}>{title}</div><div style={{ fontSize: '12px', color: '#495057', lineHeight: '1.8' }}>{desc}</div></div>;
const SaveBtn = ({ saving, onClick, label }) => <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="btn btn-primary" onClick={onClick} disabled={saving}>{saving ? 'Saving…' : label}</button></div>;

const HOURS = Array.from({ length: 24 }, (_, h) => {
  const label = h === 0 ? '12:00 AM' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`;
  return { value: `${String(h).padStart(2, '0')}:00`, label };
});
const HourSelect = ({ value, onChange }) => {
  const hour = value ? value.slice(0, 2) + ':00' : '00:00';
  return (
    <select
      className="form-control"
      value={hour}
      onChange={e => onChange(e.target.value)}
      style={{ maxWidth: '120px', fontSize: '12px', padding: '4px 8px' }}
    >
      {HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
    </select>
  );
};

export default function HubManagement() {
  const [hubs, setHubs] = useState([]);
  const [selectedHubId, setSelectedHubId] = useState('');
  const [activeTab, setActiveTab] = useState('Delivery Fees');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [general, setGeneral] = useState(DEFAULT_GENERAL);
  const [newHoliday, setNewHoliday] = useState('');
  const [fees, setFees] = useState(DEFAULT_FEES);
  const [handling, setHandling] = useState(DEFAULT_HANDLING);
  const [pincodes, setPincodes] = useState([]);
  const [newPincode, setNewPincode] = useState('');
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [pinFilter, setPinFilter] = useState('');
  const [globalPin, setGlobalPin] = useState('');
  const [globalSearching, setGlobalSearching] = useState(false);
  const [globalResults, setGlobalResults] = useState(null); // null = not searched yet; { pincode, hubs: [] }
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false); // collapsed by default to save space
  const [categories, setCategories] = useState([]);
  const [selectedHubMeta, setSelectedHubMeta] = useState(null); // hub_type, parent info, delivery_days
  const [showCreateVirtualModal, setShowCreateVirtualModal] = useState(false);
  const [virtualForm, setVirtualForm] = useState({ name: '', distance_slab: 'NEAR_100', delivery_days: [], cutoff_time: '18:00', cutoff_day_offset: -1, max_orders_per_day: 10 });
  const [creatingVirtual, setCreatingVirtual] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => { loadHubs(); loadCategories(); }, []);
  useEffect(() => { if (selectedHubId) loadHubData(selectedHubId); }, [selectedHubId]);

  async function loadCategories() {
    try {
      const res = await getCategories();
      const list = res?.data || [];
      // store full objects { id, name }
      setCategories(list);
    } catch { toast.error('Failed to load categories'); }
  }

  async function loadHubs() {
    try {
      const res = await getHubs();
      const list = res?.data || [];
      setHubs(list);
      if (list.length > 0) setSelectedHubId(list[0].id);
    } catch { toast.error('Failed to load hubs'); }
  }

  async function loadHubData(hubId) {
    setLoading(true);
    try {
      const res = await getHubSettings(hubId);
      const s = res?.data || {};
      setSelectedHubMeta({
        hub_type: s.hub_type || 'PHYSICAL',
        parent_hub_id: s.parent_hub_id || null,
        parent_name: s.parent_name || null,
        display_name: s.display_name || s.name,
        distance_slab: s.distance_slab || null,
        delivery_days: s.delivery_days || [],
        cutoff_time: s.cutoff_time || '18:00',
        cutoff_day_offset: s.cutoff_day_offset ?? 0,
      });
      setGeneral({
        hubName: s.name || '',
        hubAddress: s.address || '',
        openTime: s.open_time || '10:00',
        closeTime: s.close_time || '19:00',
        holidays: s.holidays || [],
        cutoffPanel: s.cutoff_panels_kits || '15:00',
        cutoffInverter: s.cutoff_inverters || '16:00',
        pickupCutoffPanel: s.pickup_cutoff_panels_kits || '15:00',
        pickupCutoffInverter: s.pickup_cutoff_inverters || '16:00',
        supportNumber: s.support_number || '',
        maxOrdersPerDay: s.max_orders_per_day ?? 10,
        cutoffTime: s.cutoff_time || '18:00',
        cutoffDayOffset: s.cutoff_day_offset ?? 0,
        deliveryDays: s.delivery_days || [],
        distanceSlab: s.distance_slab || 'NEAR_100',
        infoText: s.info_text || '',
        vehicleNumber: s.vehicle_number || '',
      });
      // logistics is an array of WarehouseLogistics rows, one per category
      // key by category_id for easy lookup in the table
      const feesMap = {};
      const handlingMap = {};
      (s.logistics || []).forEach(row => {
        const cid = row.category_id;
        if (!feesMap[cid]) {
          feesMap[cid] = {
            fee_same_day:               row.fee_same_day               ?? '',
            fee_next_plus_1:            row.fee_next_plus_1            ?? '',
            fee_next_plus_2:            row.fee_next_plus_2            ?? '',
            far_area_surcharge:         row.far_area_surcharge         ?? '',
            delivery_quantum:           row.delivery_quantum           ?? '',
            max_daily_units:            row.max_daily_units            ?? '',
            unloading_charge_per_order: row.unloading_charge_per_order ?? ''
          };
        }
        handlingMap[cid] = row.handling_charge_per_unit ?? '';
      });
      setFees(feesMap);
      setHandling(handlingMap);
      setPincodes((s.pincodes || []).map(p => ({
        pincode: String(p.pincode),
        isFar: p.is_far ?? false,
        sharedHubs: p.shared_hubs || [],
      })));
    } catch { toast.error('Failed to load hub settings'); }
    finally { setLoading(false); }
  }

  function addHoliday() {
    if (!newHoliday) return;
    if (general.holidays.includes(newHoliday)) { toast.warning('Date already added'); return; }
    setGeneral(g => ({ ...g, holidays: [...g.holidays, newHoliday].sort() }));
    setNewHoliday('');
  }

  async function saveGeneral() {
    setSaving(true);
    try {
      const payload = {
        name: general.hubName,
        full_address: general.hubAddress,
        open_time: general.openTime,
        close_time: general.closeTime,
        holidays: general.holidays,
        cutoff_panels_kits: general.cutoffPanel,
        cutoff_inverters: general.cutoffInverter,
        pickup_cutoff_panels_kits: general.pickupCutoffPanel,
        pickup_cutoff_inverters: general.pickupCutoffInverter,
        support_number: general.supportNumber,
        max_orders_per_day: general.maxOrdersPerDay,
        info_text: general.infoText,
        vehicle_number: general.vehicleNumber,
      };
      // Virtual hub extra fields
      if (selectedHubMeta?.hub_type === 'VIRTUAL') {
        payload.cutoff_time = general.cutoffTime;
        payload.cutoff_day_offset = general.cutoffDayOffset;
        payload.distance_slab = general.distanceSlab;
        payload.delivery_days = general.deliveryDays;
      }
      await updateHubGeneral(selectedHubId, payload);
      toast.success('General settings saved');
    }
    catch (e) { toast.error(e.message || 'Failed to save'); }
    finally { setSaving(false); }
  }

  async function saveDeliveryFees() {
    setSaving(true);
    try {
      const logistics = categories.map(cat => ({
        category_id:                cat.id,
        fee_same_day:               Number(fees[cat.id]?.fee_same_day)               || 0,
        fee_next_plus_1:            Number(fees[cat.id]?.fee_next_plus_1)            || 0,
        fee_next_plus_2:            Number(fees[cat.id]?.fee_next_plus_2)            || 0,
        far_area_surcharge:         Number(fees[cat.id]?.far_area_surcharge)         || 0,
        delivery_quantum:           Number(fees[cat.id]?.delivery_quantum)           || 1,
        max_daily_units:            Number(fees[cat.id]?.max_daily_units)            || 0,
        unloading_charge_per_order: Number(fees[cat.id]?.unloading_charge_per_order) || 0,
      }));
      await updateHubDeliveryFees(selectedHubId, { logistics });
      toast.success('Delivery fees saved');
    }
    catch (e) { toast.error(e.message || 'Failed to save'); }
    finally { setSaving(false); }
  }

  async function saveHandling() {
    setSaving(true);
    try {
      const logistics = categories.map(cat => ({
        category_id:               cat.id,
        handling_charge_per_unit:  handling[cat.id] ?? 0,
      }));
      await updateHubHandlingCharges(selectedHubId, { logistics });
      toast.success('Handling charges saved');
    }
    catch (e) { toast.error(e.message || 'Failed to save'); }
    finally { setSaving(false); }
  }

  async function handleAddPincode() {
    const pin = newPincode.trim();
    if (!/^\d{6}$/.test(pin)) { toast.error('Enter a valid 6-digit pincode'); return; }
    if (pincodes.find(p => p.pincode === pin)) { toast.warning('Pincode already exists'); return; }
    setPincodeLoading(true);
    try {
      const res = await addHubPincode(selectedHubId, pin);
      const addedPincode = res?.data || {};
      setPincodes(prev => [...prev, {
        pincode: String(addedPincode.pincode || pin),
        isFar: addedPincode.is_far ?? false,
        sharedHubs: addedPincode.shared_hubs || [],
      }]);
      setNewPincode('');
      toast.success('Pincode added');
    } catch (e) { toast.error(e.message || 'Failed to add'); }
    finally { setPincodeLoading(false); }
  }

  async function handleRemovePincode(pin) {
    if (!window.confirm('Remove pincode ' + pin + '?')) return;
    setPincodeLoading(true);
    try {
      await removeHubPincode(selectedHubId, pin);
      setPincodes(prev => prev.filter(p => p.pincode !== pin));
      toast.success('Pincode removed');
    } catch (e) { toast.error(e.message || 'Failed to remove'); }
    finally { setPincodeLoading(false); }
  }

  async function handleToggleFar(pin, currentFar) {
    setPincodeLoading(true);
    try {
      await togglePincodeFarArea(selectedHubId, pin, !currentFar);
      setPincodes(prev => prev.map(p => p.pincode === pin ? { ...p, isFar: !currentFar } : p));
    } catch (e) { toast.error(e.message || 'Failed to update'); }
    finally { setPincodeLoading(false); }
  }

  async function handleGlobalSearch() {
    const pin = globalPin.trim();
    if (!/^\d{6}$/.test(pin)) { toast.error('Enter a valid 6-digit pincode'); return; }
    setGlobalSearching(true);
    try {
      const res = await searchHubPincode(pin);
      setGlobalResults(res?.data || { pincode: pin, hubs: [] });
    } catch (e) {
      toast.error(e.message || 'Search failed');
      setGlobalResults(null);
    }
    finally { setGlobalSearching(false); }
  }

  const filteredPincodes = pincodes.filter(p =>
    p.pincode && String(p.pincode).includes(pinFilter.trim())
  );

  return (
    <div className="container">
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className="page-title">Hub Management</h1>
            <p className="page-subtitle">Configure hub settings, delivery fees, and serviceable pincodes</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ fontSize: '13px', fontWeight: '500', color: '#495057', marginBottom: 0 }}>Hub:</label>
            <select className="form-control" style={{ width: '260px', fontSize: '13px' }} value={selectedHubId} onChange={e => setSelectedHubId(e.target.value)}>
              {hubs.map(h => (
                <React.Fragment key={h.id}>
                  <option value={h.id}>{h.name}</option>
                  {(h.virtualHubs || []).map(vh => (
                    <option key={vh.id} value={vh.id}>&nbsp;&nbsp;↳ {vh.display_name}</option>
                  ))}
                </React.Fragment>
              ))}
            </select>
            {selectedHubMeta?.hub_type === 'PHYSICAL' && (
              <button className="btn btn-info" style={{ fontSize: '12px', whiteSpace: 'nowrap' }} onClick={() => setShowCreateVirtualModal(true)}>
                + Virtual Hub
              </button>
            )}
            {selectedHubMeta?.hub_type === 'VIRTUAL' && (
              <span style={{ fontSize: '11px', padding: '4px 8px', background: '#e0f2fe', color: '#0369a1', borderRadius: '4px', fontWeight: '600' }}>VIRTUAL</span>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '2px', background: 'white', border: '1px solid #e9ecef', borderRadius: '6px', padding: '4px', marginBottom: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', ...(isMobile ? { overflowX: 'auto' } : {}) }}>
        {TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{ flex: isMobile ? '0 0 auto' : 1, ...(isMobile ? { whiteSpace: 'nowrap' } : {}), padding: '8px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', transition: 'all 0.15s', background: activeTab === tab ? '#0d6efd' : 'transparent', color: activeTab === tab ? 'white' : '#495057' }}>{tab}</button>
        ))}
      </div>

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>Loading settings…</div>
      ) : (
        <>
          {activeTab === 'General' && renderGeneral()}
          {activeTab === 'Delivery Fees' && renderDeliveryFees()}
          {activeTab === 'Handling Charges' && renderHandlingCharges()}
          {activeTab === 'Delivery Pincodes' && renderPincodes()}
        </>
      )}

      {renderCreateVirtualModal()}
    </div>
  );

  function renderGeneralSoon() {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}>
        <div style={{ fontSize: '64px', marginBottom: '20px' }}>🚧</div>
        <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#212529', marginBottom: '10px' }}>Coming Soon</h2>
        <p style={{ fontSize: '14px', color: '#6c757d', maxWidth: '380px', margin: '0 auto 28px', lineHeight: '1.7' }}>
          General hub settings are in development. Check back soon — we're working on something great.
        </p>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#f0f7ff', border: '1px solid #b6d4fe', borderRadius: '20px', padding: '8px 20px' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#0d6efd', display: 'inline-block', animation: 'pulse 1.5s infinite' }} />
          <span style={{ fontSize: '13px', color: '#0d6efd', fontWeight: '500' }}>In Development</span>
        </div>
        <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
      </div>
    );
  }

  function renderGeneral() {
    return (
      <div className="card">
        {/* Hub Identity */}
        <h3 className="section-title">Hub Identity</h3>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label className="info-label">Hub Name</label>
            <input className="form-control" value={general.hubName}
              onChange={e => setGeneral(g => ({ ...g, hubName: e.target.value }))} />
          </div>
          <div>
            <label className="info-label">Hub Address</label>
            <input className="form-control" value={general.hubAddress}
              onChange={e => setGeneral(g => ({ ...g, hubAddress: e.target.value }))} />
          </div>
          <div>
            <label className="info-label">Support Number</label>
            <input className="form-control" placeholder="+91 XXXXX XXXXX" value={general.supportNumber}
              onChange={e => setGeneral(g => ({ ...g, supportNumber: e.target.value }))} />
          </div>
          <div>
            <label className="info-label">Vehicle Number</label>
            <input className="form-control" placeholder="e.g. HR 26 AB 1234" value={general.vehicleNumber}
              onChange={e => setGeneral(g => ({ ...g, vehicleNumber: e.target.value.toUpperCase() }))} maxLength={20} />
            <small style={{ color: '#6c757d', fontSize: '11px', marginTop: '4px', display: 'block' }}>
              Auto-assigned to delivery orders from this hub
            </small>
          </div>
          <div className="info-item" style={{ gridColumn: '1 / -1' }}>
            <label className="info-label">Info Text</label>
            <input className="form-control" placeholder="e.g. dispatch from below location" value={general.infoText}
              onChange={e => setGeneral(g => ({ ...g, infoText: e.target.value }))} maxLength={500} />
            <small style={{ color: '#6c757d', fontSize: '11px', marginTop: '4px', display: 'block' }}>
              Shown to customers on the warehouse selection screen (max 500 chars)
            </small>
          </div>
          {/* <div className="info-item">
            <label className="info-label">Max Orders Per Day</label>
            <input type="number" min="1" className="form-control" value={general.maxOrdersPerDay}
              onChange={e => setGeneral(g => ({ ...g, maxOrdersPerDay: parseInt(e.target.value) || 1 }))} />
            <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Next slot shifts to following day when limit is reached
            </small>
          </div> */}
        </div>

        {/* Operating Hours */}
        <h3 className="section-title" style={{ marginTop: '24px' }}>Operating Hours</h3>
        <div className="order-info">
          <div className="info-item">
            <label className="info-label">Opens At</label>
            <input type="time" className="form-control" value={general.openTime}
              onChange={e => setGeneral(g => ({ ...g, openTime: e.target.value }))} />
          </div>
          <div className="info-item">
            <label className="info-label">Closes At</label>
            <input type="time" className="form-control" value={general.closeTime}
              onChange={e => setGeneral(g => ({ ...g, closeTime: e.target.value }))} />
          </div>
        </div>

        {/* Cut-off Times */}
        <div style={{ marginTop: '16px', border: '1px solid #e9ecef', borderRadius: '6px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={{ padding: '8px 12px', fontWeight: '600', color: '#495057', textAlign: 'left', borderBottom: '1px solid #e9ecef', width: '30%' }}>Product Type</th>
                <th style={{ padding: '8px 12px', fontWeight: '600', color: '#495057', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>Delivery Cut-off <span style={{ fontWeight: '400', color: '#6c757d' }}>(same-day)</span></th>
                <th style={{ padding: '8px 12px', fontWeight: '600', color: '#495057', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>Pickup Cut-off <span style={{ fontWeight: '400', color: '#6c757d' }}>(same-day)</span></th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '8px 12px', color: '#212529', fontWeight: '500' }}>Panels / Kits</td>
                <td style={{ padding: '6px 12px' }}>
                  <HourSelect value={general.cutoffPanel} onChange={v => setGeneral(g => ({ ...g, cutoffPanel: v }))} />
                </td>
                <td style={{ padding: '6px 12px' }}>
                  <HourSelect value={general.pickupCutoffPanel} onChange={v => setGeneral(g => ({ ...g, pickupCutoffPanel: v }))} />
                </td>
              </tr>
              <tr>
                <td style={{ padding: '8px 12px', color: '#212529', fontWeight: '500' }}>Inverters</td>
                <td style={{ padding: '6px 12px' }}>
                  <HourSelect value={general.cutoffInverter} onChange={v => setGeneral(g => ({ ...g, cutoffInverter: v }))} />
                </td>
                <td style={{ padding: '6px 12px' }}>
                  <HourSelect value={general.pickupCutoffInverter} onChange={v => setGeneral(g => ({ ...g, pickupCutoffInverter: v }))} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Holidays */}
        <h3 className="section-title" style={{ marginTop: '24px' }}>Holidays</h3>
        <p style={{ color: '#6c757d', marginBottom: '16px', fontSize: '13px' }}>
          Hub closure dates. Customers see a banner 1 day before and on the day of closure.
        </p>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <input type="date" className="form-control" style={{ maxWidth: '200px' }}
            value={newHoliday} onChange={e => setNewHoliday(e.target.value)} />
          <button className="btn btn-primary" onClick={addHoliday}>+ Add Date</button>
        </div>
        {general.holidays.length === 0 ? (
          <p style={{ color: '#adb5bd', fontSize: '13px' }}>No holidays configured.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {general.holidays.map(d => (
              <span key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '20px', padding: '5px 14px', fontSize: '12px', fontWeight: '500', color: '#856404' }}>
                {new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                <button onClick={() => setGeneral(g => ({ ...g, holidays: g.holidays.filter(x => x !== d) }))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#856404', fontSize: isMobile ? '20px' : '15px', lineHeight: 1, padding: 0, ...(isMobile ? { minWidth: '32px', minHeight: '32px' } : {}) }}>×</button>
              </span>
            ))}
          </div>
        )}

        {/* Virtual Hub Delivery Schedule (only for virtual hubs) */}
        {selectedHubMeta?.hub_type === 'VIRTUAL' && (
          <>
            <h3 className="section-title" style={{ marginTop: '24px' }}>Delivery Schedule</h3>
            <p style={{ color: '#6c757d', marginBottom: '12px', fontSize: '13px' }}>
              Virtual hubs deliver on specific weekdays only. Orders must be placed by the cutoff time the previous day.
            </p>
            <div className="order-info">
              <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                <label className="info-label">Delivery Days</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                  {WEEKDAYS.map((day, idx) => (
                    <button key={idx} type="button"
                      onClick={() => setGeneral(g => ({
                        ...g,
                        deliveryDays: g.deliveryDays.includes(idx)
                          ? g.deliveryDays.filter(d => d !== idx)
                          : [...g.deliveryDays, idx]
                      }))}
                      style={{ padding: '5px 12px', fontSize: '12px', border: '1px solid', borderRadius: '4px', cursor: 'pointer',
                        background: general.deliveryDays.includes(idx) ? '#0d6efd' : '#fff',
                        color: general.deliveryDays.includes(idx) ? '#fff' : '#333',
                        borderColor: general.deliveryDays.includes(idx) ? '#0d6efd' : '#ccc'
                      }}>
                      {day}
                    </button>
                  ))}
                </div>
              </div>
              <div className="info-item">
                <label className="info-label">Order Cutoff Time</label>
                <HourSelect value={general.cutoffTime} onChange={v => setGeneral(g => ({ ...g, cutoffTime: v }))} />
                <small style={{ color: '#6c757d', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                  Previous day cutoff — orders after this time roll to the next delivery day
                </small>
              </div>
              <div className="info-item">
                <label className="info-label">Distance Slab</label>
                <select className="form-control" value={general.distanceSlab} onChange={e => setGeneral(g => ({ ...g, distanceSlab: e.target.value }))}>
                  <option value="NEAR_100">Near (0-100 km)</option>
                  <option value="FAR_200">Far (100-200 km)</option>
                </select>
              </div>
              <div className="info-item">
                <label className="info-label">Max Orders Per Trip Day</label>
                <input type="number" min="1" className="form-control" style={{ width: '120px' }} value={general.maxOrdersPerDay}
                  onChange={e => setGeneral(g => ({ ...g, maxOrdersPerDay: parseInt(e.target.value) || 1 }))} />
              </div>
            </div>
            {selectedHubMeta?.parent_name && (
              <div style={{ marginTop: '12px', padding: '8px 12px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '4px', fontSize: '12px', color: '#0369a1' }}>
                ℹ️ Inventory, billing & support inherited from parent hub: <strong>{selectedHubMeta.parent_name}</strong>
              </div>
            )}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e9ecef' }}>
          <button className="btn btn-success" onClick={saveGeneral} disabled={saving} style={{ padding: '8px 24px' }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    );
  }

  function renderDeliveryFees() {
    const isVirtual = selectedHubMeta?.hub_type === 'VIRTUAL';

    return (
      <>
        <div className="card">
          <ST c="Fee Structure" />
          {isVirtual && (
            <div style={{ marginBottom: '14px', padding: '10px 14px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', fontSize: '12px', color: '#856404' }}>
              Virtual hubs always use same-day delivery charges regardless of delivery slot selected by the customer. Next +1 and Next +2 columns are not applicable.
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr>
                  <th style={TH}>Category</th>
                  <th style={TH}>Same Day (₹)</th>
                  {!isVirtual && <th style={TH}>Next +1 (₹)</th>}
                  {!isVirtual && <th style={TH}>Next +2 (₹)</th>}
                  <th style={TH}>Far Surcharge (₹)</th>
                  <th style={TH}>Quantum (units)</th>
                  <th style={TH}>Max Daily Units</th>
                  <th style={TH}>Unloading Charge (₹)</th>
                </tr>
              </thead>
              <tbody>
                {categories.map(cat => {
                  const mkInput = (field) => (
                    <input type="number" min="0" className="form-control" style={{ width: '90px', fontSize: '13px' }}
                      value={fees[cat.id]?.[field] ?? ''}
                      onChange={e => setFees(f => ({
                        ...f,
                        [cat.id]: { ...f[cat.id], [field]: e.target.value }
                      }))} />
                  );
                  return (
                    <tr key={cat.id}>
                      <td style={{ ...TD, fontWeight: '600', color: '#212529' }}>{cat.name}</td>
                      <td style={TD}>{mkInput('fee_same_day')}</td>
                      {!isVirtual && <td style={TD}>{mkInput('fee_next_plus_1')}</td>}
                      {!isVirtual && <td style={TD}>{mkInput('fee_next_plus_2')}</td>}
                      <td style={TD}>{mkInput('far_area_surcharge')}</td>
                      <td style={TD}>{mkInput('delivery_quantum')}</td>
                      <td style={TD}>{mkInput('max_daily_units')}</td>
                      <td style={TD}>{mkInput('unloading_charge_per_order')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <ST c="Slot Definitions" />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : (isVirtual ? '1fr' : 'repeat(3, 1fr)'), gap: '12px' }}>
            {[
              { label: 'Same Day', desc: 'Order placed before cut-off today' },
              ...(!isVirtual ? [
                { label: 'Next +1', desc: 'One day after the Same Day slot' },
                { label: 'Next +2', desc: 'Two days after the Same Day slot' }
              ] : [])
            ].map(s => (
              <div key={s.label} style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '6px', padding: '12px' }}>
                <div style={{ fontWeight: '600', fontSize: '13px', color: '#212529', marginBottom: '4px' }}>{s.label}</div>
                <div style={{ fontSize: '12px', color: '#6c757d' }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
        {/* <div className="card">
          <ST c="Quantum & Multi-Category Rules" />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
            <InfoBox title="Quantum Formula" desc={<>Effective fee = <code style={{ background: '#e9ecef', padding: '1px 5px', borderRadius: '3px' }}>ceil(qty / quantum) × base fee</code><br /><span style={{ color: '#6c757d' }}>41 panels, quantum 40 → ceil(41/40) = 2 → fee × 2</span></>} />
            <InfoBox title="Multi-Category Rule" desc={<>Highest fee wins — fees are not stacked.<br /><span style={{ color: '#6c757d' }}>41 panels (₹5,000) + 1 inverter (₹200) → customer pays ₹5,000</span></>} />
          </div>
        </div> */}
        <SaveBtn saving={saving} onClick={saveDeliveryFees} label="💾 Save Delivery Fees" />
      </>
    );
  }

  function renderHandlingCharges() {
    return (
      <>
        <div className="card">
          <ST c="Handling Charges" />
          <p style={{ fontSize: '12px', color: '#6c757d', marginBottom: '14px' }}>
            Flat per-unit fee applied to every order regardless of pincode or near/far classification. Shown as a separate line item at checkout.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr>
                  <th style={TH}>Product Type</th>
                  <th style={TH}>Per-Unit Charge (₹)</th>
                  <th style={{ ...TH, color: '#6c757d' }}>Example (3 units)</th>
                </tr>
              </thead>
              <tbody>
                {categories.map(cat => (
                  <tr key={cat.id}>
                    <td style={{ ...TD, fontWeight: '600', color: '#212529' }}>{cat.name}</td>
                    <td style={TD}>
                      <input type="number" min="0" className="form-control" style={{ width: '120px', fontSize: '13px' }}
                        value={handling[cat.id] ?? ''} onChange={e => setHandling(h => ({ ...h, [cat.id]: e.target.value }))} />
                    </td>
                    <td style={{ ...TD, color: '#6c757d', fontSize: '12px' }}>3 × ₹{handling[cat.id] || 0} = ₹{3 * (Number(handling[cat.id]) || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card" style={{ background: '#f8f9fa' }}>
          <ST c="Key Rules" />
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#495057', lineHeight: '2' }}>
            <li>No impact from pincode or near/far area — handling charge is uniform</li>
            <li>Additive to delivery fees: total logistics cost = delivery fee + handling charges</li>
            <li>Changes take effect immediately for new orders; existing confirmed orders are unaffected</li>
          </ul>
        </div>
        <SaveBtn saving={saving} onClick={saveHandling} label="💾 Save Handling Charges" />
      </>
    );
  }

  function renderPincodes() {
    const farCount = pincodes.filter(p => p.isFar).length;
    return (
      <>
        <div className="card" style={{ padding: '12px 16px' }}>
          <div
            onClick={() => setGlobalSearchOpen(o => !o)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}
          >
            <span style={{ fontSize: '12px', color: '#6c757d', transform: globalSearchOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▶</span>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#212529' }}>🔍 Find Pincode Across All Hubs</span>
            <span style={{ fontSize: '11px', color: '#6c757d', marginLeft: 'auto' }}>{globalSearchOpen ? 'Hide' : 'Show'}</span>
          </div>

          {globalSearchOpen && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <input className="form-control" style={{ width: '100%', maxWidth: '200px', fontSize: '13px' }} placeholder="Enter 6-digit pincode"
                  value={globalPin}
                  onChange={e => setGlobalPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyDown={e => e.key === 'Enter' && handleGlobalSearch()} />
                <button className="btn btn-primary" onClick={handleGlobalSearch} disabled={globalSearching}>
                  {globalSearching ? 'Searching…' : 'Search'}
                </button>
                {globalResults && (
                  <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '6px 12px' }}
                    onClick={() => { setGlobalResults(null); setGlobalPin(''); }}>
                    Clear
                  </button>
                )}
              </div>
              {globalResults && (
                globalResults.hubs?.length ? (
                  <div style={{ overflowX: 'auto', marginTop: '12px' }}>
                    <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px' }}>
                      Pincode <strong style={{ color: '#212529', fontFamily: 'monospace' }}>{globalResults.pincode}</strong> is served by <strong style={{ color: '#212529' }}>{globalResults.hubs.length}</strong> hub(s):
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr>
                          <th style={TH}>Hub</th>
                          <th style={TH}>City</th>
                          <th style={TH}>State</th>
                          <th style={TH}>Area Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {globalResults.hubs.map(h => (
                          <tr key={h.hubId} style={{ background: h.isFar ? '#fff8f0' : 'white' }}>
                            <td style={{ ...TD, fontWeight: '600', color: '#212529' }}>
                              {h.hubName}
                              {h.hubType === 'VIRTUAL' && (
                                <span style={{ marginLeft: '6px', fontSize: '10px', padding: '2px 6px', background: '#e0f2fe', color: '#0369a1', borderRadius: '4px', fontWeight: '600' }}>VIRTUAL</span>
                              )}
                            </td>
                            <td style={TD}>{h.city || '—'}</td>
                            <td style={TD}>{h.state || '—'}</td>
                            <td style={TD}>
                              {h.isFar
                                ? <span className="status-badge" style={{ background: '#fd7e14', color: 'white' }}>📍 Far Area</span>
                                : <span className="status-badge" style={{ background: '#198754', color: 'white' }}>✅ Near Area</span>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p style={{ color: '#6c757d', fontSize: '13px', textAlign: 'center', padding: '16px' }}>
                    No hub serves pincode <strong style={{ fontFamily: 'monospace' }}>{globalResults.pincode}</strong>.
                  </p>
                )
              )}
            </div>
          )}
        </div>

        <div className="card">
          <ST c="Delivery Pincodes" />
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input className="form-control" style={{ width: '100%', maxWidth: '180px', fontSize: '13px' }} placeholder="Add 6-digit pincode"
              value={newPincode} onChange={e => setNewPincode(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddPincode()} />
            <button className="btn btn-primary" onClick={handleAddPincode} disabled={pincodeLoading}>+ Add Pincode</button>
            <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '6px 12px' }} onClick={() => {
              const csv = 'pincode,area_type\n' + pincodes.map(p => `${p.pincode},${p.isFar ? 'Far' : 'Near'}`).join('\n');
              const blob = new Blob([csv], { type: 'text/csv' });
              saveBlob(blob, `pincodes_hub_${selectedHubId}.csv`);
            }}>⬇ Download CSV</button>
            <input className="form-control" style={{ width: '100%', maxWidth: '200px', fontSize: '13px', marginLeft: 'auto' }} placeholder="🔍 Filter pincodes…"
              value={pinFilter} onChange={e => setPinFilter(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
            <span style={{ fontSize: '12px', color: '#6c757d' }}>Total: <strong style={{ color: '#212529' }}>{pincodes.length}</strong></span>
            <span style={{ fontSize: '12px', color: '#6c757d' }}>Near: <strong style={{ color: '#198754' }}>{pincodes.length - farCount}</strong></span>
            <span style={{ fontSize: '12px', color: '#6c757d' }}>Far: <strong style={{ color: '#fd7e14' }}>{farCount}</strong></span>
          </div>
          {filteredPincodes.length === 0 ? (
            <p style={{ color: '#6c757d', fontSize: '13px', textAlign: 'center', padding: '24px' }}>No pincodes found.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr>
                    <th style={TH}>Pincode</th>
                    <th style={TH}>Area Type</th>
                    <th style={TH}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPincodes.map(p => (
                    <tr key={p.pincode} style={{ background: p.isFar ? '#fff8f0' : 'white' }}>
                      <td style={{ ...TD, fontWeight: '600', color: '#212529', fontFamily: 'monospace', fontSize: '14px' }}>
                        {p.pincode}
                        {p.sharedHubs?.length > 0 && (
                          <span style={{ marginLeft: '6px', color: '#0d6efd', fontFamily: 'inherit', fontSize: '13px' }}>
                            ({p.sharedHubs.map(hub => hub.name).join(', ')})
                          </span>
                        )}
                      </td>
                      <td style={TD}>
                        {p.isFar
                          ? <span className="status-badge" style={{ background: '#fd7e14', color: 'white' }}>📍 Far Area</span>
                          : <span className="status-badge" style={{ background: '#198754', color: 'white' }}>✅ Near Area</span>
                        }
                      </td>
                      <td style={TD}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button className="btn btn-warning" style={{ fontSize: isMobile ? '13px' : '12px', padding: isMobile ? '8px 12px' : '4px 10px' }}
                            onClick={() => handleToggleFar(p.pincode, p.isFar)} disabled={pincodeLoading}>
                            {p.isFar ? '↩ Mark Near' : '📍 Mark Far'}
                          </button>
                          <button className="btn btn-secondary" style={{ fontSize: isMobile ? '13px' : '12px', padding: isMobile ? '8px 12px' : '4px 10px', background: '#dc3545', borderColor: '#dc3545' }}
                            onClick={() => handleRemovePincode(p.pincode)} disabled={pincodeLoading}>
                            🗑 Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="card" style={{ background: '#f8f9fa' }}>
          <ST c="Near vs Far Area Rules" />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', fontSize: '13px', color: '#495057' }}>
            <div><strong style={{ color: '#198754' }}>✅ Near Area</strong><br />Base delivery fee applies. No surcharge.</div>
            <div><strong style={{ color: '#fd7e14' }}>📍 Far Area</strong><br />Base delivery fee + far area surcharge (configured in Delivery Fees tab).</div>
          </div>
        </div>
      </>
    );
  }

  function renderCreateVirtualModal() {
    if (!showCreateVirtualModal) return null;

    const handleCreate = async () => {
      if (!virtualForm.name.trim()) { toast.error('Name is required'); return; }
      if (virtualForm.delivery_days.length === 0) { toast.error('Select at least one delivery day'); return; }
      setCreatingVirtual(true);
      try {
        const { apiCall, API_BASE_URL } = await import('../config/api');
        const res = await apiCall(`${API_BASE_URL}/hubs/oms/${selectedHubId}/virtual`, {
          method: 'POST',
          body: JSON.stringify(virtualForm)
        });
        if (res.success) {
          toast.success('Virtual hub created');
          setShowCreateVirtualModal(false);
          setVirtualForm({ name: '', distance_slab: 'NEAR_100', delivery_days: [], cutoff_time: '18:00', cutoff_day_offset: -1, max_orders_per_day: 10 });
          await loadHubs();
          if (res.data?.id) setSelectedHubId(res.data.id);
        } else {
          toast.error(res.message || 'Failed to create');
        }
      } catch (e) { toast.error(e.message || 'Failed'); }
      finally { setCreatingVirtual(false); }
    };

    const toggleDay = (day) => {
      setVirtualForm(prev => ({
        ...prev,
        delivery_days: prev.delivery_days.includes(day)
          ? prev.delivery_days.filter(d => d !== day)
          : [...prev.delivery_days, day]
      }));
    };

    return (
      <div className="modal-overlay" onClick={() => setShowCreateVirtualModal(false)}>
        <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
          <div className="modal-header">
            <h3>Create Virtual Hub under {hubs.find(h => h.id == selectedHubId)?.name}</h3>
            <button className="modal-close" onClick={() => setShowCreateVirtualModal(false)}>×</button>
          </div>
          <div className="modal-body">
            <FG label="Hub Name *" hint="Will display as 'ParentName-ThisName Hub'">
              <input className="form-control" value={virtualForm.name} onChange={e => setVirtualForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Aligarh" />
            </FG>
            <FG label="Distance Slab">
              <select className="form-control" value={virtualForm.distance_slab} onChange={e => setVirtualForm(p => ({ ...p, distance_slab: e.target.value }))}>
                <option value="NEAR_100">Near (0-100 km) — 1-2% markup</option>
                <option value="FAR_200">Far (100-200 km) — 3-4% markup</option>
              </select>
            </FG>
            <FG label="Delivery Days *" hint="Select which weekdays deliveries are scheduled">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                {WEEKDAYS.map((day, idx) => (
                  <button key={idx} type="button" onClick={() => toggleDay(idx)}
                    style={{ padding: isMobile ? '8px 14px' : '4px 10px', fontSize: isMobile ? '14px' : '12px', border: '1px solid', borderRadius: '4px', cursor: 'pointer',
                      background: virtualForm.delivery_days.includes(idx) ? '#0d6efd' : '#fff',
                      color: virtualForm.delivery_days.includes(idx) ? '#fff' : '#333',
                      borderColor: virtualForm.delivery_days.includes(idx) ? '#0d6efd' : '#ccc'
                    }}>
                    {day.substring(0, 3)}
                  </button>
                ))}
              </div>
            </FG>
            <FG label="Order Cutoff Time" hint="Orders must be placed by this time the previous day">
              <HourSelect value={virtualForm.cutoff_time} onChange={v => setVirtualForm(p => ({ ...p, cutoff_time: v }))} />
            </FG>
            <FG label="Max Orders Per Day">
              <input type="number" className="form-control" style={{ width: '120px' }} value={virtualForm.max_orders_per_day} onChange={e => setVirtualForm(p => ({ ...p, max_orders_per_day: parseInt(e.target.value) || 10 }))} />
            </FG>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={() => setShowCreateVirtualModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleCreate} disabled={creatingVirtual}>{creatingVirtual ? 'Creating...' : 'Create Virtual Hub'}</button>
          </div>
        </div>
      </div>
    );
  }
}
